import { DELAY_ESTIMATION_URL } from '../config.js';
import { fetchCache } from '../utils/fetchCache.js';
import { adoptWaitEstimate, remainingWaitSeconds } from './waitEstimate.js';

export const WAIT_NEAR_ETA_S = 20;
export const WAIT_FAR_POLL_MS = 15000;
export const WAIT_NEAR_POLL_MS = 3000;
export const WAIT_MAX_MS = 10 * 60 * 1000;

export const parseOutputKeyFromPendingUrl = (pendingUrl) => {
  if (typeof pendingUrl !== 'string') return null;
  const match = pendingUrl.match(/\/s\/temp\/([0-9a-f-]{36})\.webp/i);
  return match?.[1] ? match[1].toLowerCase() : null;
};

const fetchWaitState = ({ outputKey, pendingUrl, style } = {}) => {
  const body = {};
  if (outputKey) body.output_key = outputKey;
  if (pendingUrl) body.pending_url = pendingUrl;
  if (style) body.style = style;
  return fetchCache.fetchJSON(`${DELAY_ESTIMATION_URL}/estimate-image-duration`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    skipCache: true,
  });
};

const delay = (ms, { signal } = {}) => new Promise((resolve, reject) => {
  if (signal?.aborted) {
    reject(new DOMException('Aborted', 'AbortError'));
    return;
  }
  const timer = setTimeout(resolve, ms);
  const handleAbort = () => {
    clearTimeout(timer);
    reject(new DOMException('Aborted', 'AbortError'));
  };
  signal?.addEventListener('abort', handleAbort, { once: true });
});

const waitUntilVisible = ({ signal } = {}) => new Promise((resolve, reject) => {
  if (typeof document === 'undefined' || !document.hidden) {
    resolve();
    return;
  }
  if (signal?.aborted) {
    reject(new DOMException('Aborted', 'AbortError'));
    return;
  }
  const handleVisibility = () => {
    if (document.hidden) return;
    document.removeEventListener('visibilitychange', handleVisibility);
    signal?.removeEventListener('abort', handleAbort);
    resolve();
  };
  const handleAbort = () => {
    document.removeEventListener('visibilitychange', handleVisibility);
    reject(new DOMException('Aborted', 'AbortError'));
  };
  document.addEventListener('visibilitychange', handleVisibility);
  signal?.addEventListener('abort', handleAbort, { once: true });
});

export const waitForQueuedImage = async ({
  pendingUrl,
  outputKey = null,
  style = null,
  signal,
  onEstimate,
  requireHd = false,
  nearEtaSeconds = WAIT_NEAR_ETA_S,
  farPollMs = WAIT_FAR_POLL_MS,
  nearPollMs = WAIT_NEAR_POLL_MS,
  maxWaitMs = WAIT_MAX_MS,
} = {}) => {
  const resolvedOutputKey = outputKey || parseOutputKeyFromPendingUrl(pendingUrl);
  if (!resolvedOutputKey && !pendingUrl) {
    throw new Error('Missing output key');
  }

  const startedAt = Date.now();
  let waitEstimate = null;

  const nextPollMs = () => {
    const remaining = remainingWaitSeconds(waitEstimate);
    if (remaining == null) return farPollMs;
    if (remaining <= nearEtaSeconds) return nearPollMs;
    return farPollMs;
  };

  while (Date.now() - startedAt < maxWaitMs) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    await waitUntilVisible({ signal });

    let data = null;
    try {
      data = await fetchWaitState({
        outputKey: resolvedOutputKey,
        pendingUrl,
        style,
      });
    } catch {
      await delay(nearPollMs, { signal });
      continue;
    }

    if (typeof onEstimate === 'function') onEstimate(data);

    if (data?.status === 'completed') {
      if (requireHd && !data.is_hd) {
        throw new Error('HD upgrade failed');
      }
      return pendingUrl;
    }
    if (data?.status === 'failed') throw new Error('Image generation failed');

    if (data?.estimated_seconds >= 0) {
      waitEstimate = adoptWaitEstimate(waitEstimate, data);
    }

    await delay(nextPollMs(), { signal });
  }

  throw new Error('Image not ready after 10 minutes');
};
